import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Menu,
  Search,
  ShoppingCart,
  X
} from "lucide-react";
import { logo, ramadan } from "../../../assets/images";
import Image from "../../designLayouts/Image";
import useFetchPost from "../../../hooks/useFetchPost";

const Header = () => {
  const { t, i18n } = useTranslation();
  const [sidenav, setSidenav] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchEndpoint, setSearchEndpoint] = useState(null);
  const sidenavRef = useRef(null);

  const navigate = useNavigate();
  const searchRef = useRef();
  const products = useSelector((state) => state.orebiReducer.products);

  const { data: searchData, loading, error } = useFetchPost(
    searchEndpoint ? `/app/search_products` : null,
    searchQuery  // Modified to match API expectations
    // { search_query: searchQuery } // Modified to match API expectations
  );

  // Restore and improve click-outside handling
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the sidenav and the menu button
      if (sidenavRef.current && !sidenavRef.current.contains(event.target) &&
        !event.target.closest('button[aria-label="Open menu"]')) {
        setSidenav(false);
      }
    };

    // Only add listener when sidenav is open
    if (sidenav) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidenav]);

  // Handle search results click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setFilteredProducts([]);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter products based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts([]);
      setSearchEndpoint(null);
      return;
    }

    // Minimum 3 characters before searching
    if (searchQuery.trim().length < 3) {
      setFilteredProducts([]);
      return;
    }

    const debounceTimer = setTimeout(() => {
      setSearchEndpoint(searchQuery);
    }, 500); // Increased debounce time

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Handle search results
  useEffect(() => {
    if (searchData?.status === "success" && searchData?.data) {
      setFilteredProducts(searchData.data);
    } else if (searchData?.status === "error") {
      setFilteredProducts([]);
      console.error("Search error:", searchData?.message);
    } else {
      setFilteredProducts([]);
    }
  }, [searchData]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const closeSidenav = () => {
    setSidenav(false);
  };

  // Mobile navigation menu
  const MobileNav = () => (
    <div
      className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${sidenav ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
    >
      <div
        ref={sidenavRef}
        className={`fixed top-0 right-0 w-72 h-full bg-white transform transition-transform duration-300 ease-in-out ${sidenav ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex justify-end p-4 border-b">
            <button
              onClick={() => setSidenav(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
              aria-label="Close menu"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-6">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `block py-2 text-lg font-medium ${isActive ? "text-[#004aad]" : "text-gray-600"
                }`
              }
              onClick={closeSidenav}
            >
              {t("header.Home")}
            </NavLink>
            <NavLink
              to="/shop"
              className={({ isActive }) =>
                `block py-2 text-lg font-medium ${isActive ? "text-[#004aad]" : "text-gray-600"
                }`
              }
              onClick={closeSidenav}
            >
              {t("header.Shop")}
            </NavLink>
          </nav>

          <div className="border-t p-4">
            <select
              className="w-full p-2 bg-gray-50 rounded-lg border-none text-gray-800 focus:outline-none"
              value={i18n.language}
              onChange={(e) => {
                i18n.changeLanguage(e.target.value);
                setSidenav(false);
              }}
            >
              <option value="ar">العربية</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const SearchResults = () => (
    <div className="absolute w-full bg-white mt-2 rounded-xl shadow-lg border border-gray-100 max-h-96 overflow-y-auto">
      {loading ? (
        <div className="p-4 text-center text-gray-500">{t("header.searching")}</div>
      ) : error ? (
        <div className="p-4 text-center text-red-500">{t("header.searchError")}</div>
      ) : filteredProducts.length > 0 ? (
        filteredProducts.map((item) => (
          <div
            key={item.id}
            onClick={() => {
              navigate(`/product/${item.slug}`, {
                state: { item }
              });
              setSearchQuery("");
              setFilteredProducts([]);
            }}
            className="flex items-center gap-4 p-3 hover:bg-gray-50 cursor-pointer"
          >
            <img
              className="w-16 h-16 object-cover rounded-lg border border-gray-100"
              src={item.images[0]?.image || 'placeholder-image-url'}
              alt={item.name}
            />
            <div>
              <p className="font-medium text-gray-800">{item.name}</p>
              <p className="text-[#004aad] font-medium mt-1">${item.price}</p>
            </div>
          </div>
        ))
      ) : (
        searchQuery.length >= 3 && (
          <div className="p-4 text-center text-gray-500">
            {t("header.noProductsFound")}
          </div>
        )
      )}
    </div>
  );

  return (
    <div className="sticky top-0 z-50 bg-white shadow-sm">


      {/* Main Header */}
      <div className="w-full bg-white">
        <div className="max-w-container mx-auto px-4">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <Image className="w-16 lg:w-18 object-cover" imgSrc={logo} />
            </Link>

            {/* Desktop Search */}
            <div className="hidden lg:block flex-grow max-w-2xl mx-8">
              <div className="relative" ref={searchRef}>
                <input
                  className="w-full h-11 pl-12 pr-4 rounded-lg bg-[#f3f4f7] focus:ring-2 focus:ring-[#004aad]/20 outline-none"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder={t("header.searchPlaceholder")}
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

                {(filteredProducts.length > 0 || (loading && searchQuery)) && <SearchResults />}
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <select
                className="hidden lg:block bg-transparent border-none text-black focus:outline-none cursor-pointer"
                value={i18n.language}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
              >
                <option value="ar">العربية</option>
                <option value="en">English</option>
              </select>

              <Link to="/cart" className="relative flex items-center">
                <ShoppingCart className="w-6 h-6 text-black" />
                {products.length > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#FF9933] text-white text-xs font-medium rounded-full flex items-center justify-center">
                    {products.length}
                  </span>
                )}
                <span className="hidden lg:block ml-3">
                  <span className="text-gray-400 text-xs">{t("header.yourCart")}</span>
                  <p className="font-medium text-gray-700">$0.00</p>
                </span>
              </Link>

              <button
                onClick={() => setSidenav(true)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-full"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>



          </div>

          {/* Mobile Search */}
          <div className="lg:hidden pb-4">
            <div className="relative" ref={searchRef}>
              <input
                className="w-full h-11 pl-12 pr-4 rounded-lg bg-[#f3f4f7] focus:ring-2 focus:ring-[#004aad]/20 outline-none"
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder={t("header.searchPlaceholder")}
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

              {(filteredProducts.length > 0 || (loading && searchQuery)) && <SearchResults />}
            </div>
          </div>


        </div>

      </div>

      {/* Desktop Navigation */}
      <div className="hidden lg:block border-t border-gray-200">
        <div className="max-w-container mx-auto px-4">
          <div className="flex items-center h-14">
            <div className="flex items-center gap-12">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `text-lg font-medium transition-all duration-200 ${isActive ? "text-[#004aad]" : "text-gray-600 hover:text-[#004aad]"
                  }`
                }
              >
                {t("header.Home")}
              </NavLink>
              <NavLink
                to="/shop"
                className={({ isActive }) =>
                  `text-lg font-medium transition-all duration-200 ${isActive ? "text-[#004aad]" : "text-gray-600 hover:text-[#004aad]"
                  }`
                }
              >
                {t("header.Shop")}
              </NavLink>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <MobileNav />

    </div>
  );
};

export default Header;